import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Instagram, BarChart3, Target, TrendingUp, Loader2, Eye, DollarSign, LogOut, User, Trash2, Download, Info, Settings, FileText, HelpCircle, Sparkles, UserCheck, ChevronDown, Menu, X, CheckSquare, Square } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import LandingNavbar from "@/components/LandingNavbar";
import Footer from "@/components/Footer";
import NavTabs from "@/components/NavTabs";
import OnboardingTutorial, { isTutorialCompleted } from "@/components/OnboardingTutorial";
import TrialBanner from "@/components/TrialBanner";

// Refactored Components
import { 
  AnalysisForm, 
  AnalysisResults, 
  UserStatusBar, 
  // Modals
  QuotaModal,
  LogoutConfirmModal,
  CreditConfirmModal,
  BulkDeleteConfirmModal
} from "@/components/HomePage";

// Error Handler Utility
import { getReadableErrorMessage } from "@/utils/errorHandler";
import { getDefaultCurrency as getDefaultCurrencyForLocale } from "@/utils/currency";

// Custom Hooks
import { useAnalyzeReel } from "@/hooks/useAnalyzeReel";
import { useQueryHistory } from "@/hooks/useQueryHistory";
import { useSubscription } from "@/hooks/useSubscription";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL, API } from "@/utils/apiConfig";

// Wave effect for buttons
const buttonWaveStyles = `
  @keyframes buttonWave {
    0%, 100% {
      transform: scale(1);
    }
    25% {
      transform: scale(1.25);
    }
    50%, 100% {
      transform: scale(1);
    }
  }
  
  .button-wave-text {
    display: inline-flex;
    gap: 0;
  }
  
  .button-wave-text span {
    display: inline-block;
    animation: buttonWave 4s ease-in-out infinite;
  }
  
  /* Stagger the wave effect for each letter */
  .button-wave-text span:nth-child(1) { animation-delay: 0s; }
  .button-wave-text span:nth-child(2) { animation-delay: 0.05s; }
  .button-wave-text span:nth-child(3) { animation-delay: 0.1s; }
  .button-wave-text span:nth-child(4) { animation-delay: 0.15s; }
  .button-wave-text span:nth-child(5) { animation-delay: 0.2s; }
  .button-wave-text span:nth-child(6) { animation-delay: 0.25s; }
  .button-wave-text span:nth-child(7) { animation-delay: 0.3s; }
  .button-wave-text span:nth-child(8) { animation-delay: 0.35s; }
  .button-wave-text span:nth-child(9) { animation-delay: 0.4s; }
  .button-wave-text span:nth-child(10) { animation-delay: 0.45s; }
  .button-wave-text span:nth-child(11) { animation-delay: 0.5s; }
  .button-wave-text span:nth-child(12) { animation-delay: 0.55s; }
  .button-wave-text span:nth-child(13) { animation-delay: 0.6s; }
  .button-wave-text span:nth-child(14) { animation-delay: 0.65s; }
  .button-wave-text span:nth-child(15) { animation-delay: 0.7s; }
  
  /* Fade-in animation for loading messages */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fade-in {
    animation: fadeIn 0.5s ease-in-out;
  }
`;

function HomePage() {
  const { user, token, logout, refreshUserData, loading: authLoading, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, locale, isRTL } = useTranslation();
  
  // Helper: Get locale-aware paths for navigation
  const getLocalePath = (route) => {
    const routes = {
      en: {
        home: '/en/',
        analysis: '/en/',
        bulk: '/en/bulk-analysis',
        history: '/en/history',
        report: '/en/reports',
        pricing: '/en/pricing',
        settings: '/en/settings',
        login: '/en/login'
      },
      tr: {
        home: '/tr/',
        analysis: '/tr/',
        bulk: '/tr/toplu-analiz',
        history: '/tr/analizgecmisi',
        report: '/tr/rapor',
        pricing: '/tr/kredi-paketleri',
        settings: '/tr/hesap-ayarlari',
        login: '/tr/giris'
      },
      ar: {
        home: '/ar/',
        analysis: '/ar/',
        bulk: '/ar/bulk-analysis',
        history: '/ar/history',
        report: '/ar/reports',
        pricing: '/ar/pricing',
        settings: '/ar/settings',
        login: '/ar/login'
      },
      fr: {
        home: '/fr/',
        analysis: '/fr/',
        bulk: '/fr/bulk-analysis',
        history: '/fr/history',
        report: '/fr/reports',
        pricing: '/fr/pricing',
        settings: '/fr/settings',
        login: '/fr/login'
      },
      de: {
        home: '/de/',
        analysis: '/de/',
        bulk: '/de/bulk-analysis',
        history: '/de/history',
        report: '/de/reports',
        pricing: '/de/pricing',
        settings: '/de/settings',
        login: '/de/login'
      },
      es: {
        home: '/es/',
        analysis: '/es/',
        bulk: '/es/bulk-analysis',
        history: '/es/history',
        report: '/es/reports',
        pricing: '/es/pricing',
        settings: '/es/settings',
        login: '/es/login'
      }
    };
    
    return routes[locale]?.[route] || routes.en[route];
  };
  
  // UI State
  const [showUsernameTooltip, setShowUsernameTooltip] = useState(false);
  const [showPaymentTooltip, setShowPaymentTooltip] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // Currency state - default based on locale
  const getDefaultCurrency = () => getDefaultCurrencyForLocale(locale);
  const [currency, setCurrency] = useState(getDefaultCurrency());
  
  // PLAN F: Mode Selection State
  const [analysisMode, setAnalysisMode] = useState(null); // null | 'opara_auto' | 'manual_self'
  // Initialize with 5 empty influencer rows - each with unique object
  const [influencers, setInfluencers] = useState(Array.from({ length: 5 }, () => ({ username: '', payment: '' })));
  
  // OPARA Team: Talep Kodu Management
  const [talepKodu, setTalepKodu] = useState("");
  const [talepKoduValidation, setTalepKoduValidation] = useState(null);
  const [validatingTalepKodu, setValidatingTalepKodu] = useState(false);
  const [talepKoduQueries, setTalepKoduQueries] = useState([]);
  const [loadingTalepKoduQueries, setLoadingTalepKoduQueries] = useState(false);
  const [selectedQueriesForReport, setSelectedQueriesForReport] = useState([]);
  const [showReportSelection, setShowReportSelection] = useState(false);
  
  // Credit/Report state (estimatedCredits moved to useSubscription)
  const [pendingReportsCount, setPendingReportsCount] = useState(0);

  // =========================================
  // CUSTOM HOOK: Subscription & Modal Logic
  // =========================================
  const {
    showQuotaModal,
    setShowQuotaModal,
    showCreditConfirm,
    setShowCreditConfirm,
    showLogoutConfirm,
    setShowLogoutConfirm,
    estimatedCredits,
    setEstimatedCredits,
    getRemainingTime,
    handleUpgradeClick
  } = useSubscription();

  // =========================================
  // CUSTOM HOOK: Query History Logic
  // =========================================
  const {
    queryHistory,
    filteredHistory,
    loadingHistory,
    searchQuery,
    setSearchQuery,
    selectedQueryIds,
    setSelectedQueryIds,
    toggleQuerySelection,
    selectAllQueries,
    showBulkDeleteConfirm,
    setShowBulkDeleteConfirm,
    handleBulkDelete,
    handleHistoryClick: handleHistoryClickBase,
    refreshHistory,
    getQueryHistoryDuration
  } = useQueryHistory({
    onHistoryClick: (query, mappedResult) => {
      // Set result for display
      setResult(mappedResult);
      setAnalysisComplete(true);
      
      // Fill form with original inputs
      if (query.username) {
        setUsername(query.username);
      }
      
      if (query.payment_per_reel) {
        const payment = query.payment_per_reel;
        setPaymentPerReel(payment.toString());
        setDisplayPayment(formatCurrency(payment));
      }
    }
  });
  
  // =========================================
  // CUSTOM HOOK: Analiz Logic
  // =========================================
  const {
    username,
    setUsername,
    displayPayment,
    setDisplayPayment,
    paymentPerReel,
    setPaymentPerReel,
    handlePaymentChange,
    handlePaymentKeyDown,
    loading,
    setLoading,
    result,
    setResult,
    analysisComplete,
    setAnalysisComplete,
    handleAnalyze,
    resetForm
  } = useAnalyzeReel({
    onRefreshHistory: refreshHistory,
    talepKodu,
    talepKoduValidation,
    onFetchTalepKoduQueries: (code) => fetchTalepKoduQueries(code),
    locale,  // i18n: Pass current locale for backend evaluation labels
    currency  // secili gosterim para birimi -> persist (history dogru birim)
  });
  
  // Helper function: Calculate Follower Engagement Level based on follower count
  const calculateEngagementLevel = (followers, engagement) => {
    if (!followers || !engagement) return { rate: 0, level: 'Hesaplanamadı', color: 'text-gray-400' };
    
    const rate = (engagement / followers) * 100;
    
    // Determine segment and thresholds
    let lowThreshold, highThreshold;
    
    if (followers < 10000) {
      // NANO
      lowThreshold = 2.50;
      highThreshold = 4.50;
    } else if (followers < 50000) {
      // MIKRO_1
      lowThreshold = 2.25;
      highThreshold = 4.25;
    } else if (followers < 100000) {
      // MIKRO_2
      lowThreshold = 2.00;
      highThreshold = 4.00;
    } else if (followers < 200000) {
      // MAKRO_1
      lowThreshold = 1.75;
      highThreshold = 3.50;
    } else if (followers < 400000) {
      // MAKRO_2
      lowThreshold = 1.50;
      highThreshold = 3.00;
    } else if (followers < 600000) {
      // MAKRO_3
      lowThreshold = 1.25;
      highThreshold = 2.50;
    } else if (followers < 800000) {
      // MEGA_1
      lowThreshold = 1.00;
      highThreshold = 2.00;
    } else if (followers < 1000000) {
      // MEGA_2
      lowThreshold = 0.75;
      highThreshold = 1.75;
    } else if (followers < 2000000) {
      // CELEB_1
      lowThreshold = 0.50;
      highThreshold = 1.50;
    } else {
      // CELEB_2 (2M+)
      lowThreshold = 0.25;
      highThreshold = 1.25;
    }
    
    let level, color;
    if (rate < lowThreshold) {
      level = 'Düşük 🔴';
      color = 'text-red-400';
    } else if (rate <= highThreshold) {
      level = 'Ortalama 🟡';
      color = 'text-yellow-400';
    } else {
      level = 'Yüksek 🟢';
      color = 'text-green-400';
    }
    
    return { rate: (typeof rate === 'number' ? rate : parseFloat(rate)).toFixed(2), level, color };
  };

  // Handle mode query parameter (from RaporlamaPage navigation)
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'opara_auto') {
      setAnalysisMode('opara_auto');
      // Clear the query parameter
      navigate(getLocalePath('home'), { replace: true });
    }
  }, [searchParams, navigate, locale]);

  // Handle /opara-yapsin URL - Only for @personaajans.com users
  useEffect(() => {
    if (window.location.pathname === '/opara-yapsin') {
      // Check if user is authorized (personaajans.com email)
      if (user && user.email && user.email.toLowerCase().endsWith('@personaajans.com')) {
        setAnalysisMode('opara_auto');
      } else {
        // Redirect unauthorized users to home
        navigate(getLocalePath('home'));
        if (user) {
          toast.error(t('home.access_denied'));
        }
      }
    }
  }, [user, navigate, locale]);

  // Fetch pending reports count for team members
  useEffect(() => {
    const fetchPendingReportsCount = async () => {
      // Check if user is team member (@personaajans.com)
      if (user && user.email && user.email.toLowerCase().endsWith('@personaajans.com') && token) {
        try {
          const response = await axios.get(`${API}/team/pending-reports`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setPendingReportsCount(response.data.length);
        } catch (error) {
          console.error('Error fetching pending reports:', error);
          setPendingReportsCount(0);
        }
      }
    };

    fetchPendingReportsCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchPendingReportsCount, 60000); // Changed from 30s to 60s for better performance
    return () => clearInterval(interval);
  }, [user, token]);
  
  // Handle Google OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');
      const provider = params.get('provider');
      const status = params.get('status');
      
      console.log('[OAuth Debug] URL params:', { tokenFromUrl: tokenFromUrl?.substring(0, 20) + '...', provider, status });
      
      // v19.3 FIX: Token varsa işle - provider ve status opsiyonel
      if (tokenFromUrl) {
        console.log('[OAuth Debug] Token found!');
        
        // Prevent duplicate execution
        if (sessionStorage.getItem('oauth_handled') === 'true') {
          console.log('[OAuth Debug] oauth_handled is true, skipping...');
          return;
        }
        
        // v19.4 FIX: HEMEN localStorage'a yaz - redirect'ten ÖNCE
        console.log('[OAuth Debug] Saving token to localStorage IMMEDIATELY...');
        localStorage.setItem('auth_token', tokenFromUrl);
        
        sessionStorage.setItem('oauth_handled', 'true');
        console.log('[OAuth Debug] Calling loginWithToken...');
        
        try {
          const result = await loginWithToken(tokenFromUrl);
          console.log('[OAuth Debug] loginWithToken result:', result);
          
          if (result.success) {
            // Clean URL after successful login
            window.history.replaceState({}, document.title, window.location.pathname);
            toast.success(t('auth.login.success'), { duration: 2000 });
            console.log('[OAuth Debug] Login successful!');
            
            setTimeout(() => {
              sessionStorage.removeItem('oauth_handled');
            }, 3000);
          } else {
            console.error('[OAuth Debug] Login failed:', result.error);
            // Token'ı kaldır çünkü geçersiz
            localStorage.removeItem('auth_token');
            sessionStorage.removeItem('oauth_handled');
            toast.error(result.error || t('home.login_failed'));
          }
        } catch (error) {
          console.error('[OAuth Debug] Callback error:', error);
          localStorage.removeItem('auth_token');
          sessionStorage.removeItem('oauth_handled');
          toast.error(t('auth.generic_error'));
        }
      }
    };
    
    handleOAuthCallback();
  }, [loginWithToken]);

  // Handle talep_kodu from URL (OPARA Team workflow)
  useEffect(() => {
    const talepKoduFromUrl = searchParams.get('talep_kodu');
    
    if (talepKoduFromUrl && user && token && user.email && user.email.toLowerCase().endsWith('@personaajans.com')) {
      // Only validate if it's different from current talepKodu
      if (talepKoduFromUrl !== talepKodu) {
        setTalepKodu(talepKoduFromUrl);
        // Auto-validate
        validateTalepKoduHandler(talepKoduFromUrl);
      }
    }
  }, [searchParams, user, token]);

  // Validate talep kodu function
  const validateTalepKoduHandler = async (code) => {
    if (!code || !code.trim()) {
      setTalepKoduValidation(null);
      return;
    }

    // Check if token is available
    if (!token) {
      console.log('Token not available yet, skipping validation');
      return;
    }

    setValidatingTalepKodu(true);
    
    try {
      console.log('Validating talep kodu:', code);
      console.log('Using backend URL:', BACKEND_URL);
      const response = await axios.post(
        `${BACKEND_URL}/api/team/validate-talep-kodu`,
        { talep_kodu: code },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      console.log('Validation response:', response.data);
      
      // Only show toast if validation state changed
      const isNewValidation = !talepKoduValidation || talepKoduValidation.message !== response.data.message;
      
      setTalepKoduValidation(response.data);
      
      if (isNewValidation) {
        if (response.data.valid) {
          toast.success(response.data.message);
        } else {
          toast.error(response.data.message);
        }
      }
    } catch (error) {
      console.error('Talep kodu validation error:', error);
      console.error('Error details:', error.response?.data);
      setTalepKoduValidation({
        valid: false,
        message: error.response?.data?.detail || t('home.talep_code_failed_retry')
      });
      toast.error(error.response?.data?.detail || t('home.talep_code_failed'));
    } finally {
      setValidatingTalepKodu(false);
    }
  };


  // Fetch queries for active talep_kodu
  const fetchTalepKoduQueries = async (code) => {
    if (!code || !token) return;
    
    setLoadingTalepKoduQueries(true);
    
    try {
      console.log('Fetching queries for talep_kodu:', code);
      const response = await axios.get(
        `${BACKEND_URL}/api/team/queries-by-talep-kodu/${code}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      console.log('Queries response:', response.data);
      setTalepKoduQueries(response.data);
    } catch (error) {
      console.error('Error fetching talep kodu queries:', error);
      setTalepKoduQueries([]);
    } finally {
      setLoadingTalepKoduQueries(false);
    }
  };

  // Watch for talep kodu validation and fetch queries
  useEffect(() => {
    if (talepKoduValidation && talepKoduValidation.valid && talepKodu) {
      fetchTalepKoduQueries(talepKodu);
    } else {
      setTalepKoduQueries([]);
    }
  }, [talepKoduValidation, talepKodu, token]);



  // REMOVED: No longer redirect non-authenticated users - HomePage is now a landing page
  // Giriş yapmamış kullanıcılar artık welcome modal ile karşılanıyor

  // REMOVED: Auto-open modal when credits = 0
  // Modal should only open when user tries to make a new query with 0 credits

  // NOTE: Query history logic moved to useQueryHistory hook

  const formatNumberShort = (num) => {
    if (!num) return "0";
    if (num >= 1000000) {
      // Milyon için 1 decimal (3.0M, 1.2M)
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      // v7.5: 10K altı için 1 decimal (1.7K, 2.6K), 10K üstü için tam sayı (143K)
      const kValue = num / 1000;
      if (kValue < 10) {
        return kValue.toFixed(1) + "K";
      }
      return Math.floor(kValue) + "K";
    }
    return num.toString();
  };

  const handleExportToExcel = () => {
    if (queryHistory.length === 0) {
      toast.error(t('home.no_history_export'));
      return;
    }

    // Prepare CSV data
    const headers = [
      t('csv.username'),
      t('csv.followers'),
      t('csv.target_views'),
      t('csv.engagement_rate'),
      t('csv.cost_per_view'),
      t('csv.influencer_fee'),
      t('csv.query_date')
    ];

    const rows = queryHistory.map(query => {
      return [
        `"${query.username}"`,
        `"${(query.follower_count || 0).toLocaleString('en-US')}"`,
        `"${Math.round(query.result_data?.target_reel_views || 0).toLocaleString('en-US')}"`,
        `"${query.engagement_rate ? (typeof query.engagement_rate === 'number' ? query.engagement_rate.toFixed(2) : parseFloat(query.engagement_rate).toFixed(2)) : 0}"`,
        `"${query.result_data?.cost_per_view ? (typeof query.result_data.cost_per_view === 'number' ? query.result_data.cost_per_view.toFixed(2) : parseFloat(query.result_data.cost_per_view).toFixed(2)) : 0}"`,
        `"${(query.payment_per_reel || 0).toLocaleString('en-US')}"`,
        `"${new Date(query.query_time).toLocaleString('tr-TR')}"`
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    // Add BOM for Turkish characters
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `sorgulama_gecmisi_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(t('messages.history_downloaded'));
  };

  // Format query time: "10 dk. önce" or date if > 60 mins
  const formatQueryTime = (queryTime) => {
    const now = new Date();
    const queryDate = new Date(queryTime);
    const diffMs = now - queryDate;
    const diffMins = Math.floor(diffMs / 1000 / 60);
    
    if (diffMins < 60) {
      return `${diffMins} dk. önce`;
    } else {
      // More than 60 mins: show date only (dd.mm.yyyy)
      return queryDate.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  };

  // Plan ismi mapping
  const getPlanDisplayName = (planType) => {
    const planNames = {
      "free": "Ücretsiz",
      "package_10": "10 Analiz",
      "package_50": "50 Analiz",
      "package_100": "100 Analiz",
      "pro_monthly": "Pro Aylık",
      "business_monthly": "Business Aylık",
      "persona_staff": "PERSONA ÇALIŞAN (Sınırsız)"
    };
    return planNames[planType] || planType;
  };

  // NOTE: getRemainingTime moved to useSubscription hook

  const formatDecimal = (value, decimals = 2) => {
    // Ondalık ayracı NOKTA (.) kullan
    // Convert to number if string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return numValue.toFixed(decimals);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    
    // Handle string values with commas (from MongoDB)
    if (typeof value === 'string') {
      // Remove commas and convert to number
      const numValue = parseFloat(value.replace(/,/g, ''));
      if (isNaN(numValue)) return '-';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numValue);
    }
    
    // Use en-US for comma separator (33,000 not 33.000)
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };


  // NOTE: getQueryHistoryDuration moved to useQueryHistory hook
  
  // Handle reporting click
  const handleReportingClick = () => {
    if (!user) return;
    
    // Artık direkt rapor sayfasına git - keşif ücretsiz!
    navigate(getLocalePath('report'));
  };

  // PLAN F: Handle OPARA Auto Report Submit
  const handleOparaAutoSubmit = async () => {
    setLoading(true);
    
    try {
      // Validate influencers
      const validInfluencers = influencers.filter(inf => inf.username.trim());
      
      if (validInfluencers.length === 0) {
        toast.error(t('home.min_one_influencer'));
        setLoading(false);
        return;
      }

      // Prepare payload
      const payload = {
        influencers: validInfluencers.map(inf => ({
          username: inf.username.trim(),
          payment: inf.payment ? parseFloat(inf.payment.replace(/,/g, '')) : null
        }))
      };

      // Send request to backend
      const response = await axios.post(
        `${API}/opara-auto-report-request`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Success
      toast.success(response.data.message || t('messages.request_sent'));
      
      // Refresh user data to update credits
      await refreshUserData();
      
      // Reset form
      setInfluencers(Array.from({ length: 5 }, () => ({ username: '', payment: '' })));
      setAnalysisMode(null);
      setShowCreditConfirm(false);
      
    } catch (error) {
      console.error("OPARA Auto Report Error:", error);
      
      if (error.response?.status === 402) {
        toast.error(t('home.insufficient_credits'));
      } else if (error.response?.status === 403) {
        const _d403 = error.response?.data?.detail;
        toast.error(_d403 === 'account_frozen' ? t('errors.account_frozen') : (_d403 || t('home.pro_business_only')));
      } else if (error.response?.status === 400) {
        const errorDetail = error.response?.data?.detail || "";
        
        // Show error message directly (no more Instagram verification)
        toast.error(errorDetail);
      } else {
        toast.error(error.response?.data?.detail || t('home.request_send_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#FFDD59] animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="App">
        <Toaster position="top-center" toastOptions={{
          style: { marginTop: '40vh' }
        }} richColors closeButton />
        <div className="min-h-screen bg-black relative flex flex-col">
        {/* Background Orbs */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
          <div className="absolute top-1/4 right-1/5 w-96 h-96 opacity-20 animate-pulse">
            <div className="w-full h-full rounded-full bg-gradient-radial from-[#FFDD59]/40 via-[#FFDD59]/20 to-transparent blur-3xl"></div>
          </div>
        </div>

        {/* Header - Conditional based on user login status */}
        {!user ? (
          /* Landing Navbar for non-logged-in users */
          <LandingNavbar />
        ) : (
          /* NavTabs for logged-in users */
          <NavTabs user={user} currentPage="analiz" />
        )}

        {/* Main Content - Flex grow to push footer down */}
        <div className="flex-1">

        {/* OPARA v7.4: Trial Banner - Show for free plan users */}
        {user && user.plan_type === 'free' && (
          <div className="max-w-4xl mx-auto px-4 pt-4">
            <TrialBanner user={user} locale={locale} />
          </div>
        )}

        {/* Main Content Area */}
        {!user ? (
          /* GUEST VIEW: B2B SaaS landing (6 sections) — Commit 3r */
          <div dir={isRTL ? 'rtl' : 'ltr'} className="relative z-10 bg-[#0a0a0a]">
            <style>{`
              .opara-blur{display:inline-block;background:linear-gradient(90deg,#444,#333);color:transparent;border-radius:4px;filter:blur(3px);user-select:none;padding:0 6px;font-weight:600}
              .opara-blur-avatar{filter:blur(4px);background:linear-gradient(135deg,#2a2a2a,#1a1a1a)}
            `}</style>

            {/* SECTION 1 — HERO */}
            <section className="py-12 px-5 text-center max-w-[900px] mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold text-yellow-200 mb-4 leading-tight">{t('landing.hero_title')}</h1>
              <p className="text-sm md:text-base text-gray-300 max-w-2xl mx-auto mb-6 leading-relaxed">
                <span className="font-medium text-white">{t('landing.hero_tagline')}</span>{' '}{t('landing.hero_subtext')}
              </p>
              <div className="flex gap-3 justify-center flex-wrap mb-3">
                <Link to={getLocalePath('login')}><Button className="bg-yellow-300 hover:bg-yellow-200 text-black px-6 py-2.5 rounded-md font-semibold text-sm">{t('landing.cta_signup')}</Button></Link>
                <Link to={getLocalePath('pricing')}><Button variant="outline" className="bg-transparent border border-gray-600 text-gray-200 hover:bg-gray-800 px-5 py-2.5 rounded-md font-medium text-sm">{t('landing.cta_packages')}</Button></Link>
              </div>
              <p className="text-xs text-gray-400">🎁 {t('landing.free_trial_badge')}</p>
            </section>

            {/* SECTION 2 — OPARA'S DIFFERENCE */}
            <section className="py-10 px-5 border-t border-gray-900">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl font-semibold text-white text-center mb-3">{t('landing.diff_title')}</h2>
                <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6 max-w-2xl mx-auto">
                  <p className="text-sm md:text-base text-gray-200 leading-relaxed text-center">
                    {t('landing.diff_text_1')}<span className="text-yellow-300 font-semibold"> {t('landing.diff_highlight_1')} </span>{t('landing.diff_text_2')}<span className="text-yellow-300 font-semibold"> {t('landing.diff_highlight_2')} </span>{t('landing.diff_text_3')}
                  </p>
                </div>
              </div>
            </section>

            {/* SECTION 3 — BENEFITS */}
            <section className="py-10 px-5 border-t border-gray-900">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-xl font-semibold text-white text-center mb-2">{t('landing.benefits_title')}</h2>
                <p className="text-sm text-gray-400 text-center mb-6">{t('landing.benefits_sub')}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
                  {[
                    { icon: '⚡', tk: 'landing.benefit_1_title', dk: 'landing.benefit_1_desc' },
                    { icon: '🎯', tk: 'landing.benefit_2_title', dk: 'landing.benefit_2_desc' },
                    { icon: '📋', tk: 'landing.benefit_3_title', dk: 'landing.benefit_3_desc' },
                  ].map((b, i) => (
                    <div key={i} className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4">
                      <div className="w-8 h-8 rounded-md bg-[#FFDD59]/[0.12] flex items-center justify-center text-lg mb-2">{b.icon}</div>
                      <h3 className="text-base font-semibold text-white mb-1">{t(b.tk)}</h3>
                      <p className="text-xs text-gray-400 leading-relaxed">{t(b.dk)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* SECTION 4 — DEMO REPORT (blurred numbers, clear labels) */}
            <section className="py-10 px-5 border-t border-gray-900">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl font-semibold text-white text-center mb-2">{t('landing.demo_title')}</h2>
                <p className="text-sm text-gray-400 text-center mb-6">{t('landing.demo_sub')}</p>
                <div className="bg-gray-900 border-2 border-[#FFDD59]/40 rounded-xl p-4 sm:p-6 space-y-4">
                  {/* Profile block */}
                  <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4 grid grid-cols-1 md:grid-cols-[auto_auto_1fr] gap-4 md:gap-6 md:items-start">
                    <div className="opara-blur-avatar w-[90px] h-[90px] rounded-full border-2 border-yellow-400 shrink-0" />
                    <div className="flex flex-col gap-2 min-w-0">
                      <div><p className="text-sm text-gray-400 mb-1">{t('results.username_label')}:</p><p className="text-xl"><span className="opara-blur">@xxxxxxxxxxxx</span></p></div>
                      <div><p className="text-sm text-gray-400 mb-1">{t('results.followers_label')}:</p><p className="text-xl"><span className="opara-blur">XXXK</span></p></div>
                      <div><p className="text-sm text-gray-400 mb-1">{t('results.reel_cost_label')}:</p><p className="text-xl"><span className="opara-blur">XXX,XXX TRY</span></p></div>
                    </div>
                    <div className="flex flex-col gap-3 min-w-0">
                      <p className="text-sm text-gray-400">{t('results.branded_reel_count')}: <span className="opara-blur">X</span></p>
                      <div>
                        <p className="text-sm text-gray-400">{t('results.brands_section_title')}</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
                          {Array.from({ length: 6 }).map((_, i) => (<span key={i} className="opara-blur text-sm">@xxxxxxxx</span>))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Current Performance — show all 3 evaluation colors */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-300 mb-3">{t('results.current_performance_section_title')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { label: 'results.reel_activity', badge: '🟡', word: 'landing.demo_eval_avg', color: 'text-yellow-400' },
                        { label: 'results.visibility_performance', badge: '🟢', word: 'landing.demo_eval_high', color: 'text-green-400' },
                        { label: 'results.engagement_section_title', badge: '🔴', word: 'landing.demo_eval_low', color: 'text-red-400' },
                      ].map((b, i) => (
                        <div key={i} className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
                          <p className="text-sm text-gray-400 mb-1">{t(b.label)}:</p>
                          <p className={`text-xl font-bold ${b.color}`}>{t(b.word)} {b.badge}</p>
                          <p className="text-xs mt-2"><span className="opara-blur">xxxxxxxxxxxxxxxx</span></p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Forecast */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-300 mb-3">{t('results.collaboration_section_title')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { label: 'results.expected_views_label', val: 'XXXK - X.XM' },
                        { label: 'results.total_engagement', val: 'XX.XK - XX.XK' },
                        { label: 'results.story_views_label', val: 'XXK - XXXK' },
                      ].map((b, i) => (
                        <div key={i} className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
                          <p className="text-sm text-gray-400 mb-1">{t(b.label)}:</p>
                          <p className="text-2xl"><span className="opara-blur">{b.val}</span></p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* SECTION 5 — AUDIENCE */}
            <section className="py-10 px-5 border-t border-gray-900">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl font-semibold text-white text-center mb-2">{t('landing.audience_title')}</h2>
                <p className="text-sm text-gray-400 text-center mb-6">{t('landing.audience_sub')}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-[#1a1a1a] border border-yellow-300 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">🎯</div>
                    <h3 className="text-sm font-semibold text-white mb-1">{t('landing.audience_brands_name')}</h3>
                    <p className="text-xs text-gray-400">{t('landing.audience_brands_desc')}</p>
                  </div>
                  <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">📊</div>
                    <h3 className="text-sm font-semibold text-white mb-1">{t('landing.audience_agencies_name')}</h3>
                    <p className="text-xs text-gray-400">{t('landing.audience_agencies_desc')}</p>
                  </div>
                  <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">💼</div>
                    <h3 className="text-sm font-semibold text-white mb-1">{t('landing.audience_consultants_name')}</h3>
                    <p className="text-xs text-gray-400">{t('landing.audience_consultants_desc')}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* SECTION 6 — FINAL CTA */}
            <section className="py-12 px-5 text-center border-t border-gray-900">
              <div className="max-w-[900px] mx-auto">
                <h2 className="text-xl font-semibold text-white mb-2">{t('landing.final_title')}</h2>
                <p className="text-sm text-gray-400 mb-4">{t('landing.final_sub')}</p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Link to={getLocalePath('login')}><Button className="bg-yellow-300 hover:bg-yellow-200 text-black px-6 py-2.5 rounded-md font-semibold text-sm">{t('landing.cta_signup')}</Button></Link>
                  <Link to={getLocalePath('pricing')}><Button variant="outline" className="bg-transparent border border-gray-600 text-gray-200 hover:bg-gray-800 px-5 py-2.5 rounded-md font-medium text-sm">{t('landing.cta_packages')}</Button></Link>
                </div>
                <p className="text-xs text-gray-500 italic max-w-xl mx-auto mt-4 leading-relaxed">{t('landing.disclaimer')}</p>
              </div>
            </section>
          </div>
        ) : (
          /* LOGGED-IN VIEW: Full Width Analysis */
          <div className="flex justify-center relative z-10">
            <main className={`w-full pb-16 px-4 md:px-8 ${
              talepKoduValidation && talepKoduValidation.valid && talepKodu 
                ? 'max-w-4xl'
                : 'max-w-[50rem]'
            }`}>
              {/* Main Title - Centered */}
              <div className="mb-8 pt-8">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center shadow-lg">
                    <Instagram className="w-4 h-4 text-black" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#FFDD59] whitespace-pre-line text-center">
                    {t('home.analysis_title')}
                  </h2>
                </div>
                
                {/* New Analysis Button - Show if there are results */}
                {result && (
                  <div className="flex justify-center mt-4">
                    <Button
                      onClick={resetForm}
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700 text-white border-none text-base font-medium"
                      data-testid="new-analysis-btn"
                    >
                      <span className="button-wave-text">
                        {(t('home.new_analysis_btn') || 'Yeni Analiz').split('').map((char, i) => (
                          <span key={i}>{char === ' ' ? '\u00A0' : char}</span>
                        ))}
                      </span>
                    </Button>
                  </div>
                )}
              </div>

            {/* Landing Page Info Sections removed - Now in Welcome Modal */}

            {/* User Status Bar - Analiz Hakkı ve Dunning durumu */}
            <UserStatusBar user={user} />

            {/* Input Card - Show based on mode */}
            {(analysisMode === 'manual_self' || analysisMode === null || user?.plan_type === "free") && (
              <AnalysisForm
                username={username}
                setUsername={setUsername}
                displayPayment={displayPayment}
                handlePaymentChange={handlePaymentChange}
                handlePaymentKeyDown={handlePaymentKeyDown}
                currency={currency}
                setCurrency={setCurrency}
                loading={loading}
                analysisComplete={analysisComplete}
                handleAnalyze={handleAnalyze}
                user={user}
                talepKodu={talepKodu}
                setTalepKodu={setTalepKodu}
                talepKoduValidation={talepKoduValidation}
                setTalepKoduValidation={setTalepKoduValidation}
                validateTalepKoduHandler={validateTalepKoduHandler}
                validatingTalepKodu={validatingTalepKodu}
              />
            )}

            {/* PLAN F: OPARA Auto Report Form */}
            {analysisMode === 'opara_auto' && (
              <Card className="mb-8 bg-gray-900 border-2 border-[#FFDD59]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-bold text-[#FFDD59] text-left">
                    <div>{t('homepage.promo_title1')}</div>
                    <div className="mt-2">{t('homepage.promo_title2')}</div>
                  </CardTitle>
                  {/* Bilgilendirme Başlığı */}
                  <div className="mt-2 text-left">
                    <p className="text-sm text-gray-300">
                      {t('homepage.info_prefix')}{' '}
                      <span 
                        className="text-[#FFDD59] font-semibold cursor-help underline hover:text-[#E6C84D] relative group"
                      >
                        {t('homepage.info_link')}
                      {/* Birleşik Tooltip */}
                      <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-96 bg-gray-800 border-2 border-[#FFDD59] rounded-lg p-5 text-sm text-left opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                        {/* Hizmet Kapsamı Bölümü */}
                        <div className="mb-4">
                          <h3 className="text-[#FFDD59] font-bold text-base mb-3">{t('homepage.scope_title')}</h3>
                          <ul className="space-y-2 text-gray-200">
                            <li className="flex items-start gap-2">
                              <span className="text-[#FFDD59] mt-1">•</span>
                              <span>{t('homepage.scope_item1')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-[#FFDD59] mt-1">•</span>
                              <span>{t('homepage.scope_item2')}</span>
                            </li>
                          </ul>
                        </div>
                        
                        {/* Ücretlendirme Bölümü */}
                        <div className="border-t border-gray-700 pt-4">
                          <h3 className="text-[#FFDD59] font-bold text-base mb-3">{t('homepage.pricing_title')}</h3>
                          <table className="w-full text-gray-200">
                            <tbody>
                              <tr className="border-b border-gray-700">
                                <td className="py-2 pr-4 text-gray-400">{t('homepage.price_per_query_label')}</td>
                                <td className="py-2 text-right font-bold text-[#FFDD59]">{t('homepage.price_per_query_value')}</td>
                              </tr>
                              <tr className="border-b border-gray-700">
                                <td className="py-2 pr-4 text-gray-400">{t('homepage.price_report_label')}</td>
                                <td className="py-2 text-right font-bold text-[#FFDD59]">{t('homepage.price_report_value')}</td>
                              </tr>
                              <tr>
                                <td className="py-2 pr-4 text-gray-400">{t('homepage.price_min_label')}</td>
                                <td className="py-2 text-right font-bold text-green-400">{t('homepage.price_min_value')}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </span>
                    </span>
                  </p>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Influencer Table - Always visible with 10 initial rows */}
                    <div className="space-y-2">
                      {influencers.map((inf, index) => {
                        return (
                          <div key={index} className="grid grid-cols-12 gap-2 items-start bg-gray-800 p-2 rounded-md">
                            <div className="col-span-1 text-center pt-2">
                              <span className="text-[#FFDD59] font-semibold text-sm">{index + 1}</span>
                            </div>
                            <div className="col-span-6">
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">@</span>
                                <Input
                                  type="text"
                                  value={inf.username}
                                  onChange={(e) => {
                                    const newInfluencers = [...influencers];
                                    newInfluencers[index].username = e.target.value;
                                    setInfluencers(newInfluencers);
                                  }}
                                  placeholder={t('home.bulk_username_placeholder')}
                                  className="pl-6 text-white text-sm h-9 bg-gray-700 border-gray-600"
                                />
                              </div>
                            </div>
                            <div className="col-span-4">
                              <Input
                                type="text"
                                value={inf.payment ? formatCurrency(inf.payment.replace(/,/g, '')) : ''}
                                onChange={(e) => {
                                  const rawValue = e.target.value.replace(/,/g, ''); // Virgülleri kaldır
                                  if (rawValue === '' || /^\d+$/.test(rawValue)) { // Sadece rakam veya boş
                                    const newInfluencers = [...influencers];
                                    newInfluencers[index].payment = rawValue;
                                    setInfluencers(newInfluencers);
                                  }
                                }}
                                placeholder={t('home.bulk_payment_placeholder')}
                                className="bg-gray-700 border-gray-600 text-white text-sm h-9"
                              />
                            </div>
                            <div className="col-span-1 text-center">
                              <Button
                                onClick={() => {
                                  const newInfluencers = influencers.filter((_, i) => i !== index);
                                  setInfluencers(newInfluencers);
                                }}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-9 w-9 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      </div>

                      {/* Add Influencer Button - After first 5 rows */}
                      {influencers.length >= 5 && (
                        <div className="flex justify-center mt-4">
                          <Button
                            onClick={() => {
                              const maxInfluencers = 20;
                              if (influencers.length < maxInfluencers) {
                                setInfluencers([...influencers, { username: '', payment: '' }]);
                              } else {
                                toast.error(t('home.max_20_influencers'), {
                                  duration: 5000
                                });
                              }
                            }}
                            className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black"
                          >
                            + Influencer Ekle
                          </Button>
                        </div>
                      )}

                      {/* Submit Button */}
                      <div className="mt-6">
                        <div className="flex gap-3">
                          <Button
                            onClick={() => {
                              // Check if user is logged in
                              if (!user || !token) {
                                toast.error(t('notifications.login_required'), {
                                  duration: 3000
                                });
                                return;
                              }
                              
                              // Count filled influencers
                              const filledInfluencers = influencers.filter(inf => inf.username.trim());
                              if (filledInfluencers.length === 0) {
                                toast.error(t('home.min_one_influencer'));
                                return;
                              }
                              
                              // Validate Instagram usernames
                              const instagramUsernameRegex = /^(?!.*\.\.)(?!.*\._)(?!.*_\.)[a-zA-Z0-9._]{1,30}(?<!\.)$/;
                              const invalidUsernames = [];
                              
                              filledInfluencers.forEach((inf, index) => {
                                const username = inf.username.trim();
                                // Check if username is valid
                                if (!instagramUsernameRegex.test(username)) {
                                  const originalIndex = influencers.findIndex(i => i.username === inf.username);
                                  invalidUsernames.push(`${originalIndex + 1}. ${username}`);
                                }
                              });
                              
                              if (invalidUsernames.length > 0) {
                                const errorMessage = `❌ Geçersiz kullanıcı adları tespit edildi:\n\n${invalidUsernames.join('\n')}\n\nInstagram kullanıcı adı kuralları: 1-30 karakter, sadece harf, rakam, nokta (.) ve alt çizgi (_) içerebilir. Nokta ile bitemez, ardışık nokta veya nokta+alt çizgi kombinasyonu içeremez.`;
                                
                                toast.error(errorMessage, { 
                                  duration: 8000,
                                  style: {
                                    whiteSpace: 'pre-line'
                                  }
                                });
                                return;
                              }
                              
                              const calculatedCredits = (filledInfluencers.length * 3) + 30;
                              const finalCredits = Math.max(calculatedCredits, 50);
                              setEstimatedCredits(finalCredits);
                              setShowCreditConfirm(true);
                            }}
                            className="flex-1 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold h-12"
                          >
                            <Sparkles className="w-5 h-5 mr-2" />
                            {t('home.opara_auto_btn')}
                          </Button>
                          <Button
                            onClick={() => {
                              setAnalysisMode(null);
                              setInfluencers(Array.from({ length: 5 }, () => ({ username: '', payment: '' })));
                            }}
                            variant="outline"
                            className="border-gray-600 text-gray-300 hover:bg-gray-800"
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results Card - Refactored Component */}
            {result && (
              <AnalysisResults result={result} currency={currency} />
            )}
          </main>
          
          {/* Right Sidebar - Query History for Talep Kodu (Only for team members with valid talep_kodu) */}
          {talepKoduValidation && talepKoduValidation.valid && talepKodu && (
            <div className="w-80 border-l border-purple-500/30 bg-purple-900/10 sticky top-0 h-screen overflow-y-auto">
              <div className="p-4">
                {showReportSelection && selectedQueriesForReport.length > 0 && (
                  <div className="mb-4 p-3 bg-purple-800/30 border border-purple-500 rounded-lg">
                    <p className="text-sm text-purple-200 font-semibold mb-2">
                      {selectedQueriesForReport.length} sorgu seçildi
                    </p>
                    <Button
                      onClick={() => {
                        // Navigate to reporting page with selected queries
                        navigate('/rapor', {
                          state: {
                            talepKodu: talepKodu,
                            customerName: talepKoduValidation.customer_name,
                            selectedQueryIds: selectedQueriesForReport
                          }
                        });
                      }}
                      className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
                    >
                      Raporu Oluştur →
                    </Button>
                  </div>
                )}

                {/* Queries List */}
                {loadingTalepKoduQueries ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                  </div>
                ) : talepKoduQueries.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-purple-300 text-sm mb-2">
                      Henüz sorgu yapılmadı
                    </p>
                    <p className="text-purple-400/70 text-xs">
                      Bu müşteri için sorgu yaptığınızda burada görünecek
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {talepKoduQueries.map((query) => (
                      <div
                        key={query.id}
                        className={`bg-purple-800/20 rounded-lg p-3 border transition-all ${
                          showReportSelection
                            ? selectedQueriesForReport.includes(query.id)
                              ? 'border-[#FFDD59] bg-yellow-900/20'
                              : 'border-purple-700/50 cursor-pointer hover:border-purple-500'
                            : 'border-purple-700/50'
                        }`}
                        onClick={() => {
                          if (showReportSelection) {
                            setSelectedQueriesForReport(prev =>
                              prev.includes(query.id)
                                ? prev.filter(id => id !== query.id)
                                : [...prev, query.id]
                            );
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {query.profile_picture && (
                              <img
                                src={`https://wsrv.nl/?url=${encodeURIComponent(query.profile_picture)}&w=200&h=200&fit=cover`}
                                alt={query.username}
                                className="w-8 h-8 rounded-full"
                              />
                            )}
                            <p className="text-white font-semibold text-sm">@{query.username}</p>
                          </div>
                          {showReportSelection && (
                            <div>
                              {selectedQueriesForReport.includes(query.id) ? (
                                <CheckSquare className="w-5 h-5 text-[#FFDD59]" />
                              ) : (
                                <Square className="w-5 h-5 text-purple-500" />
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-purple-300">👥 {query.follower_count?.toLocaleString('en-US')}</span>
                            {query.engagement_rate && (
                              <>
                                <span className="text-purple-500">•</span>
                                <span className="text-purple-300">ER: {(typeof query.engagement_rate === 'number' ? query.engagement_rate : parseFloat(query.engagement_rate)).toFixed(2)}%</span>
                              </>
                            )}
                          </div>
                          
                          {query.result_data && (
                            <div className="flex items-center gap-2">
                              <span className="text-blue-400">
                                📊 {Math.round(query.result_data.target_reel_views || 0).toLocaleString('en-US')}
                              </span>
                              {query.result_data.cost_per_view && (
                                <>
                                  <span className="text-purple-500">•</span>
                                  <span className="text-green-400">
                                    💰 {(typeof query.result_data.cost_per_view === 'number' ? query.result_data.cost_per_view : parseFloat(query.result_data.cost_per_view)).toFixed(4)} ₺
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          
                          {query.query_time && (
                            <p className="text-purple-400 text-xs mt-1">
                              🕐 {formatQueryTime(query.query_time)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
        </div>
        )}

        {/* Footer */}

        {/* Modals - Refactored to Components */}
        <BulkDeleteConfirmModal
          isOpen={showBulkDeleteConfirm}
          onClose={() => setShowBulkDeleteConfirm(false)}
          onConfirm={handleBulkDelete}
          selectedCount={selectedQueryIds.length}
        />

        <LogoutConfirmModal
          isOpen={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={() => {
            logout();
            navigate(getLocalePath('login'));
          }}
        />

        <CreditConfirmModal
          isOpen={showCreditConfirm}
          onClose={() => setShowCreditConfirm(false)}
          onConfirm={handleOparaAutoSubmit}
          onUpgrade={() => {
            setShowCreditConfirm(false);
            navigate(getLocalePath('pricing'));
          }}
          estimatedCredits={estimatedCredits}
          userCredits={user?.credits_remaining || 0}
          loading={loading}
        />

      {/* Quota Modal */}
      <QuotaModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        onUpgrade={handleUpgradeClick}
      />

      {/* Auth Modal removed - Using /giris page instead */}
      
      {/* Onboarding Tutorial - Sadece yeni üye olmuş ve ilk kez giriş yapan kullanıcılara */}
      {user && user.total_queries_made === 0 && <OnboardingTutorial isNewUser={!isTutorialCompleted()} />}
      
      </div>
      {/* End of flex-1 content wrapper */}
      
      {/* Footer - Same across all pages */}
      <Footer />
      
      {/* Language Sidebar removed - now in navbar dropdown */}
      </div>
      </div>
    </TooltipProvider>
  );
}

export default HomePage;
